import React, { useEffect } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useProfile } from '@/hooks/use-profile';
import { useAuth } from '@/contexts/AuthContext';
import { Download, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

interface WelcomeShareCardProps {
  profile: any;
  onClose: () => void;
}

const WelcomeShareCard = ({ profile: initialProfile, onClose }: WelcomeShareCardProps) => {
  const { user } = useAuth();
  const { profile: updatedProfile } = useProfile(user?.id);
  
  // Use the most up-to-date profile data
  const profile = updatedProfile || initialProfile;

  const handleDownload = () => {
    toast.success('Screenshot this card to share!');
  };

  return (
    <div 
      className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex items-center justify-center p-4"
    >
      <div 
        className="relative w-full max-w-md animate-fade-in cursor-pointer"
        onClick={onClose}
      >
        {/* Holographic Card with gradient border */}
        <div className="relative rounded-3xl p-[2px] bg-gradient-to-br from-cyan-400 via-purple-500 to-pink-500">
          <div className="relative rounded-3xl bg-gradient-to-br from-gray-900 via-black to-gray-900 overflow-hidden">
            {/* Holographic shimmer overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-purple-500/10 to-transparent opacity-50 animate-shimmer" />
            
            {/* FOMO Logo at top */}
            <div className="flex justify-center pt-4 pb-1 relative z-10">
              <img
                src="/fomo_2025.png"
                alt="FOMO"
                className="h-16 w-auto opacity-90"
              />
            </div>

            {/* Banner Background with gradient overlay */}
            <div className="h-32 relative overflow-hidden">
              {profile?.banner_image_url ? (
                <>
                  <img
                    src={profile.banner_image_url}
                    alt="Banner"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/50 to-black" />
                </>
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-purple-900/30 via-pink-900/20 to-cyan-900/30" />
              )}
            </div>

            {/* Profile Content */}
            <div className="px-8 pb-8 -mt-16 relative z-10">
              {/* Avatar with holographic ring */}
              <div className="flex justify-center mb-4">
                <div className={cn(
                  "rounded-full p-1 bg-gradient-to-br from-cyan-400 via-purple-500 to-pink-500",
                  "shadow-[0_0_30px_rgba(168,85,247,0.4)]"
                )}>
                  <Avatar className="w-28 h-28 border-4 border-black">
                    <AvatarImage src={profile?.profile_image_url} />
                    <AvatarFallback className="text-2xl bg-gradient-to-br from-purple-600 to-pink-600">
                      {profile?.username?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </div>
              </div>

              {/* Name and badge */}
              <div className="text-center space-y-2">
                <div className="flex items-center justify-center gap-2">
                  <Sparkles className="w-4 h-4 text-yellow-400" />
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-white via-purple-200 to-pink-200 bg-clip-text text-transparent">
                    {profile?.display_name || profile?.username}
                  </h1>
                  <Sparkles className="w-4 h-4 text-yellow-400" />
                </div>
                
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 px-4 py-1.5 rounded-full">
                  <span className="text-sm font-medium text-purple-300">
                    @{profile?.username}
                  </span>
                </div>
              </div>

              {/* Stats cards */}
              <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-4 text-center">
                  <div className="text-xs text-purple-300 font-medium mb-1">Member #</div>
                  <div className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                    {profile?.registration_number}
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-xl p-4 text-center">
                  <div className="text-xs text-cyan-300 font-medium mb-1">Status</div>
                  <div className="text-sm font-bold text-cyan-400">
                    Early Access
                  </div>
                </div>
              </div>

              {/* Bottom text */}
              <div className="mt-6 text-center space-y-2">
                <div className="text-xs text-gray-500 uppercase tracking-wider font-medium">
                  Welcome to FOMO
                </div>
                <div className="text-[10px] text-gray-600">
                  Screenshot & share your fomo card
                </div>
              </div>
            </div>

            {/* Holographic corner accents */}
            <div className="absolute top-0 left-0 w-20 h-20 bg-gradient-to-br from-cyan-400/20 to-transparent rounded-br-full" />
            <div className="absolute bottom-0 right-0 w-20 h-20 bg-gradient-to-tl from-pink-400/20 to-transparent rounded-tl-full" />
          </div>
        </div>

        {/* Close hint */}
        <div className="mt-4 text-center">
          <p className="text-xs text-gray-500">Click anywhere to close</p>
        </div>
      </div>
    </div>
  );
};

export default WelcomeShareCard;